/** Production studio portal custom domain (ENR-2597). */
export const STUDIO_PORTAL_CUSTOM_DOMAIN = 'https://studio.enrollio.ai';

/** Legacy App Engine default-service host. Keep for dual-host / rollback. */
export const STUDIO_PORTAL_APPSPOT_HOST = 'enrollio-portal.uc.r.appspot.com';

export const STUDIO_PORTAL_APPSPOT_URL = `https://${STUDIO_PORTAL_APPSPOT_HOST}`;

/**
 * Prefer baked `VITE_STUDIO_PORTAL_URL`, then live origin, then custom domain.
 * Use this for shareable login links so appspot dual-host sessions still copy
 * the custom domain once Vite is baked to studio.enrollio.ai.
 */
export function resolveStudioPortalBaseUrl(
  bakedUrl = import.meta.env.VITE_STUDIO_PORTAL_URL,
): string {
  const raw = String(bakedUrl || '').trim();
  if (raw) {
    return raw.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return STUDIO_PORTAL_CUSTOM_DOMAIN;
}

export function buildStudioLoginUrl(locationId: string): string {
  return `${resolveStudioPortalBaseUrl()}/login?id=${locationId}`;
}

/**
 * Paths that must keep working on appspot while GHL / callbacks catch up.
 * Redirect flag must not break these.
 */
export const APPSPOT_REDIRECT_EXCLUDED_PATH_PREFIXES = [
  '/authorize',
  '/ghl-authorize',
  '/_ah/',
  '/health',
] as const;

export function isAppspotRedirectExcludedPath(pathname: string): boolean {
  const path = pathname || '/';
  return APPSPOT_REDIRECT_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix),
  );
}

/**
 * Client-side dual-host cutover helper (static App Engine SPA).
 * Default OFF via unset / non-"true" `VITE_REDIRECT_APPSPOT_TO_CUSTOM_DOMAIN`.
 * Returns true when navigation away was started.
 */
export function maybeRedirectAppspotToCustomDomain(input?: {
  hostname?: string;
  pathname?: string;
  search?: string;
  hash?: string;
  redirectEnabled?: boolean;
  targetBaseUrl?: string;
}): boolean {
  const redirectEnabled =
    input?.redirectEnabled ??
    String(import.meta.env.VITE_REDIRECT_APPSPOT_TO_CUSTOM_DOMAIN || '')
      .trim()
      .toLowerCase() === 'true';

  if (!redirectEnabled) {
    return false;
  }

  const hostname = (
    input?.hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '')
  ).toLowerCase();

  if (hostname !== STUDIO_PORTAL_APPSPOT_HOST) {
    return false;
  }

  const pathname =
    input?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  if (isAppspotRedirectExcludedPath(pathname)) {
    return false;
  }

  const search =
    input?.search ??
    (typeof window !== 'undefined' ? window.location.search : '');
  const hash =
    input?.hash ?? (typeof window !== 'undefined' ? window.location.hash : '');
  const targetBase = (
    input?.targetBaseUrl || STUDIO_PORTAL_CUSTOM_DOMAIN
  ).replace(/\/$/, '');
  const nextUrl = `${targetBase}${pathname}${search}${hash}`;

  if (typeof window !== 'undefined') {
    window.location.replace(nextUrl);
  }

  return true;
}
